import { useState, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { navigateToMember, navigateToSite, navigateToCommunity } from '@flexo/resolvers';
import { SelectOption, HeaderType } from '../types';

export const useHeaderSelect = (type: HeaderType) => {
  const { t } = useTranslation();
  const location = useLocation();
  const params = useParams();
  const pathSegment = location.pathname.split('/')[1];
  const [open, setOpen] = useState(false);

  const selectOptions = useMemo((): SelectOption[] => {
    switch (type) {
      case 'member-data-vis':
        return [
          {
            value: 'member-energy',
            label: t('components.navigation.options.energy'),
            disabled: pathSegment === 'member-energy',
            onClick: () => navigateToMember('member-energy', params.id as string)
          },
          {
            value: 'member-economics',
            label: t('components.navigation.options.economics'),
            disabled: pathSegment === 'member-economics',
            onClick: () => navigateToMember('member-economics', params.id as string)
          }
        ];

      case 'site':
        return [
          {
            value: 'site-energy',
            label: t('components.navigation.options.energy'),
            disabled: pathSegment === 'site-energy',
            onClick: () => navigateToSite('site-energy', params.id as string)
          },
          {
            value: 'site-economics',
            label: t('components.navigation.options.economics'),
            disabled: pathSegment === 'site-economics',
            onClick: () => navigateToSite('site-economics', params.id as string)
          }
        ];

      case 'community-data-vis':
        return [
          {
            value: 'community-energy',
            label: t('components.navigation.options.energy'),
            disabled: pathSegment === 'community-energy',
            onClick: () => navigateToCommunity('community-energy', params.id as string)
          },
          {
            value: 'community-economics',
            label: t('components.navigation.options.economics'),
            disabled: pathSegment === 'community-economics',
            onClick: () => navigateToCommunity('community-economics', params.id as string)
          }
        ];

      default:
        return [];
    }
  }, [type, pathSegment, params, t]);

  const currentSelectValue = useMemo(() => {
    if (selectOptions.length === 0) return undefined;
    return pathSegment;
  }, [pathSegment, selectOptions]);

  const handleSelectChange = (value: string) => {
    const option = selectOptions.find(opt => opt.value === value);
    if (option && option.onClick) {
      option.onClick();
    }
  };

  const triggerSelectOpen = () => {
    setOpen(!open);
  };

  return {
    selectOptions: selectOptions.length > 0 ? selectOptions : undefined,
    currentSelectValue,
    onSelectChange: handleSelectChange,
    isSelectOpen: open,
    setSelectOpen: setOpen,
    triggerSelectOpen
  };
}; 